import $ from "jquery";
import { I18n } from "react-redux-i18n";
import admin, { getNonLocalPrinterForPrinterAction } from "../admin";
import auth, { choiceDialog2, confirmDialog, featureEnabled, messageDialog, post } from "../auth";
import { getPaymentMethod, isCashLikePaymentType } from "../tableMatePaid";

const parseCashRegisterSettings = (cashierType) => {
    if (!cashierType || !cashierType.settings) {
        return {};
    }
    if (typeof cashierType.settings === "object") {
        return cashierType.settings;
    }
    if (typeof cashierType.settings !== "string") {
        return {};
    }
    try {
        const parsed = JSON.parse(cashierType.settings);
        return parsed && typeof parsed === "object" ? parsed : {};
    } catch (error) {
        return {};
    }
};

const resolveSetting = (settings, keys, fallback = null) => {
    const keyList = Array.isArray(keys) ? keys : [keys];
    for (let i = 0; i < keyList.length; i++) {
        const value = settings[keyList[i]];
        if (typeof value !== "undefined" && value !== null && value !== "") {
            return value;
        }
    }
    return fallback;
};

const formatMoney = (value) => {
    const n = Number(value || 0);
    if (Number.isNaN(n)) {
        return "0.00";
    }
    return n.toFixed(2);
};

const buildSubmitKeyRequest = (settings) => {
    const publicKey = resolveSetting(settings, ["montaxSubmitPublicKey", "submitPublicKey"], null);
    if (!publicKey || typeof publicKey !== "string") {
        return null;
    }
    return {
        publicKey,
        date: Date.now()
    };
};

const buildMontaxDocCreateRequest = (type, cashierType, amount = 0) => {
    const settings = parseCashRegisterSettings(cashierType);
    const systemId = resolveSetting(settings, ["montaxSystemId", "systemId", "ap"], "");
    const currency = resolveSetting(settings, ["montaxCurrency", "currency"], "HUF");
    const drawerName = resolveSetting(settings, ["montaxDrawerName", "drawerName"], "Készpénz");
    const moneyCat = resolveSetting(settings, ["montaxMoneyCat", "moneyCat"], "CASH");
    const dayOpenCustomInfoText = resolveSetting(settings, ["montaxDayOpenCustomText", "dayOpenCustomText"], "DAY OPEN CUSTOM INFO");
    const dayCloseCustomInfoText = resolveSetting(settings, ["montaxDayCloseCustomText", "dayCloseCustomText"], "DAY CLOSE CUSTOM INFO");

    const body = {
        "@type": type,
        systemId,
        createDownloadInfo: true
    };

    if (type === "createFiscalDayOpen") {
        body.cashDrawer = [{
            name: drawerName,
            moneyCat,
            moneySubCat: null,
            moneyAmount: formatMoney(amount),
            currency
        }];
        body.docCustomInfo = [{
            "@type": "text",
            key: "R00001_NAPNYIT_KIEG",
            description: "Leírás",
            text: dayOpenCustomInfoText,
            alignment: "CENTER",
            orderId: 1
        }];
    } else if (type === "createFiscalDayReport") {
        body.docCustomInfo = [{
            "@type": "text",
            key: "R05044_FISCALDAYCLOSE",
            description: "Leírás",
            text: dayCloseCustomInfoText,
            alignment: "CENTER",
            orderId: 1
        }];
    }

    const submitKeyRequest = buildSubmitKeyRequest(settings);
    if (submitKeyRequest) {
        body.submitKeyRequest = submitKeyRequest;
    }

    return JSON.stringify(body);
};

const buildDefaultDayOpenPayload = (cashierType, amount) => {
    return buildMontaxDocCreateRequest("createFiscalDayOpen", cashierType, amount);
};

const buildDefaultDayClosePayload = (cashierType) => {
    return buildMontaxDocCreateRequest("createFiscalDayReport", cashierType, 0);
};

const buildDefaultCashRegisterReportPayload = (cashierType) => {
    return buildMontaxDocCreateRequest("createCashRegisterReport", cashierType, 0);
};

const MONTAX_VAT_DEPARTMENTS = { 5: "A", 18: "B", 27: "C", 0: "D", undefined: "D", null: "D" };

const getMontaxDeptByVat = (vat) => {
    return MONTAX_VAT_DEPARTMENTS[vat] || "";
};

const buildMontaxReceiptItem = (amountEntry, settings = {}) => {
    if (!amountEntry || typeof amountEntry !== "object") {
        return null;
    }

    const amount = Number(amountEntry.amount || 0) + Number(amountEntry.service_amount || 0);
    const count = Number(amountEntry.count || 1);
    if (!Number.isFinite(amount) || amount <= 0) {
        return null;
    }

    const normalizedCount = Number.isFinite(count) && count > 0 ? count : 1;
    const itemName = String(amountEntry.name || "SALE").trim() || "SALE";
    const itemDept = String(amountEntry.dept || getMontaxDeptByVat(amountEntry.vat) || resolveSetting(settings, ["montaxItemDept", "itemDept"], "A")).trim() || "A";
    const itemUnit = String(resolveSetting(settings, ["montaxItemUnit", "itemUnit"], "PIECE")).trim() || "PIECE";
    const itemArticleNo = String(amountEntry.articleNo || amountEntry.barcode || `${itemName}-${String(amountEntry.vat ?? "0")}`)
        .trim() || `MONTAX-${Math.abs(Math.round(amount * 100))}`;

    return {
        itemName,
        itemArticleNo,
        itemUnitPrice: formatMoney(amount),
        itemQty: normalizedCount.toFixed(4),
        itemUnit,
        itemCat: "SALE",
        itemDept,
        itemCustomInfo: []
    };
};

const buildMontaxReceiptPayload = (cashierType, amounts = {}) => {
    const settings = parseCashRegisterSettings(cashierType);
    const systemId = resolveSetting(settings, ["montaxSystemId", "systemId", "ap"], "");
    const receiptItems = Object.values(amounts)
        .sort((a, b) => (Number(a.entryOrder || 0) - Number(b.entryOrder || 0)))
        .map((entry) => buildMontaxReceiptItem(entry, settings))
        .filter(Boolean);

    const body = {
        "@type": "createReceipt",
        systemId,
        receiptItems,
        createDownloadInfo: true
    };

    const submitKeyRequest = buildSubmitKeyRequest(settings);
    if (submitKeyRequest) {
        body.submitKeyRequest = submitKeyRequest;
    }

    return JSON.stringify(body);
};

const resolveMontaxCashRegister = (cashierType) => {
    const activeCashRegisters = Array.isArray(auth?.myStatus?.restaurant_cash_registers) ? auth.myStatus.restaurant_cash_registers : [];
    if (cashierType && typeof cashierType === "object") {
        return cashierType;
    }
    if (!cashierType) {
        return activeCashRegisters.find(c => c?.type === "montax") || null;
    }
    return activeCashRegisters.find(c => c?.name === cashierType || c?.type === cashierType) || null;
};

const buildPayloadFromSettings = (cashierType, keyCandidates, fallbackPayload, amount = 0) => {
    const settings = parseCashRegisterSettings(cashierType);
    const customPayload = resolveSetting(settings, keyCandidates, null);
    if (typeof customPayload === "string" && customPayload.trim() !== "") {
        return customPayload.replace(/\{amount\}/gi, String(amount));
    }

    return fallbackPayload;
};

const buildPattern = (cashierType) => {
    const settings = parseCashRegisterSettings(cashierType);
    const pattern = resolveSetting(settings, ["montaxPattern", "pattern"], null);
    return typeof pattern === "string" && pattern.trim() !== "" ? pattern : ".*";
};

const buildRequestOptions = (cashierType, pathKeys = [], methodKeys = []) => {
    const settings = parseCashRegisterSettings(cashierType);
    const httpPath = resolveSetting(settings, [...pathKeys, "montaxHttpPath", "httpPath"], "/doc/doc-create");
    const httpMethod = resolveSetting(settings, [...methodKeys, "montaxHttpMethod", "httpMethod"], "POST");
    return {
        httpPath,
        httpMethod: String(httpMethod || "POST").toUpperCase(),
        appendNewLine: false,
        injectSystemIdFromDevice: true,
        headers: {
            "Content-Type": "application/json"
        }
    };
};

const templateValue = (value, context = {}) => {
    if (typeof value !== "string" || !value.length) {
        return value;
    }

    let result = value;
    Object.keys(context || {}).forEach((key) => {
        const replacement = context[key];
        if (replacement === null || typeof replacement === "undefined") {
            return;
        }
        const escapedKey = key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        result = result.replace(new RegExp(`\\{${escapedKey}\\}`, "gi"), String(replacement));
    });
    return result;
};

const getPathFromUrl = (urlValue) => {
    if (typeof urlValue !== "string" || !urlValue.trim()) {
        return "";
    }
    try {
        const parsed = new URL(urlValue);
        return `${parsed.pathname || ""}${parsed.search || ""}`;
    } catch (error) {
        return "";
    }
};

const toPathToken = (value) => {
    const normalized = String(value ?? "").trim();
    if (!normalized) {
        return "";
    }
    if (/^\{[^}]+\}$/.test(normalized)) {
        return normalized;
    }
    return encodeURIComponent(normalized);
};

const buildDocumentDataPath = (context = {}, receiptType) => {
    const systemId = context.systemId || context.ap || "";
    const fiscalDayNo = context.fiscalDayNo || "";
    const documentType = context.documentType || "";
    const documentId = context.documentId || "";
    const formatType = (context.formatType || "FORMATTED").toUpperCase();

    if (!systemId || !fiscalDayNo || !documentType || !documentId) {
        return "";
    }

    return `/doc/document/${toPathToken(systemId)}/${toPathToken(fiscalDayNo)}/${toPathToken(documentType)}/${toPathToken(documentId)}/${toPathToken(formatType)}`;
};

const buildMontaxPaymentDetails = (settings, types, payable) => {
    const defaultCurrency = resolveSetting(settings, ["montaxCurrency", "currency"], "HUF");
    const baseCurrency = auth?.myStatus?.restaurant_base_currency?.name || defaultCurrency;
    const cashName = resolveSetting(settings, ["montaxDrawerName", "drawerName"], "Készpénz");
    const cashCat = resolveSetting(settings, ["montaxMoneyCat", "moneyCat"], "CASH");
    const cardName = resolveSetting(settings, ["montaxCardName", "cardName"], "Bankkártya");
    const cardCat = resolveSetting(settings, ["montaxCardMoneyCat", "cardMoneyCat"], "CARD");
    const voucherName = resolveSetting(settings, ["montaxVoucherName", "voucherName"], "Utalvány");
    const voucherCat = resolveSetting(settings, ["montaxVoucherMoneyCat", "voucherMoneyCat"], "OTHER_CHANGE_RETURNABLE");
    const voucherSubCat = resolveSetting(settings, ["montaxVoucherMoneySubCat", "voucherMoneySubCat"], "AJÁND");

    const details = (Array.isArray(types) ? types : [])
        .filter(t => Number(t?.amount) > 0)
        .map(t => {
            const payment = getPaymentMethod(t.type);
            const paymentType = payment?.method?.type ?? payment?.type ?? t.type ?? "";
            const paymentName = payment?.method?.name ?? payment?.name ?? "";
            const normalizedPaymentType = typeof paymentType === "string" ? paymentType.toLowerCase() : paymentType;
            const normalizedPaymentName = String(paymentName || "").toLowerCase();
            const isVoucher = normalizedPaymentType === "utalvany"
                || /(utalvany|utalvány|ajándékkártya|ajandekkartya|gift card|giftcard)/i.test(normalizedPaymentName);
            const isCash = isCashLikePaymentType(normalizedPaymentType);
            const displayName = isVoucher ? (paymentName || voucherName) : (isCash ? cashName : cardName);
            const moneyCat = isVoucher ? voucherCat : (isCash ? cashCat : cardCat);
            const moneySubCat = isVoucher ? voucherSubCat : null;
            return {
                isCash,
                currency: t.currency || baseCurrency,
                amount: Number(t.amount),
                name: displayName,
                moneyCat,
                moneySubCat
            };
        });

    // Cash given above the payable amount is change, only the payable part may be registered.
    let excess = details.filter(d => d.currency === baseCurrency).reduce((sum, d) => sum + d.amount, 0) - Number(payable || 0);
    if (Number(payable) > 0) {
        for (let i = details.length - 1; i >= 0 && excess > 0; i--) {
            const d = details[i];
            if (!d.isCash || d.currency !== baseCurrency) {
                continue;
            }
            const cut = Math.min(d.amount, excess);
            d.amount -= cut;
            excess -= cut;
        }
    }

    return details.filter(d => d.amount > 0).map(d => ({
        name: d.name,
        moneyCat: d.moneyCat,
        moneySubCat: d.moneySubCat,
        moneyAmount: formatMoney(d.amount),
        currency: d.currency
    }));
};

// Extra receipt texts and QR codes (order number, feedback, scan-a-bill), same content as the a234 receipt.
// TABLEMATEPAYMENTID is replaced by the server before the closeReceipt message is sent.
const MONTAX_ORDER_NUMBER_KEY = "K01001_RENDELES_SZAM";

const buildMontaxExtraCustomInfo = (tableMate, seqnr, firstOrderId = 2) => {
    const info = [];
    let orderId = firstOrderId;
    const addText = (key, text) => info.push({
        "@type": "text", key, description: "Leírás", orderId: orderId++, text, alignment: "CENTER"
    });
    const addQr = (key, data) => info.push({
        "@type": "barcodeQr", key, description: "Leírás", orderId: orderId++, data, ec: 1, size: 3, mode: 3
    });

    if (seqnr)
        addText(MONTAX_ORDER_NUMBER_KEY, `${I18n.t("admin_local.order_sequence_number")} #${seqnr}`);

    if (tableMate?.id) {
        const baseUrl = "https://restaurant.eatwithme.online" + (auth?.myStatus?.test_mode ? ":8444" : "");
        const common = `r=${auth?.myStatus?.restaurant_global_id}&t=${sessionStorage.tableNumberSelected}&m=${tableMate.id}&q=${tableMate.qrToken}`;
        if (featureEnabled("history/feedback")) {
            addText("K01002_SPACER", "\u00a0");
            addText("K01002_SCANIT", I18n.t("admin_local.scanit"));
            addText("K01003_RATEUS", I18n.t("admin_local.rateus"));
            addQr("N00004_FEEDBACK_QR", `${baseUrl}/feedback.html?${common}&p=TABLEMATEPAYMENTID`);
        } else if (!auth?.myStatus?.restaurant_settings?.["shop-mode"] && featureEnabled("history/scan-a-bill")) {
            addQr("N00004_SCAN_A_BILL_QR", `${baseUrl}/map.html?${common}&p=true&c=true&a=m`);
        }
    }
    return info;
};

// documentId is added by the server from the createReceipt response, systemId by the device layer.
const buildMontaxCloseReceiptMessage = (cashierType, types, payable, tableMate, seqnr) => {
    const settings = parseCashRegisterSettings(cashierType);
    const systemId = resolveSetting(settings, ["montaxSystemId", "systemId", "ap"], "");
    const thankYouText = resolveSetting(settings, ["montaxReceiptCustomText", "receiptCustomText"], "Köszönjük a vásárlást!");

    const body = {
        "@type": "closeReceipt",
        systemId,
        docCustomInfo: [{
            "@type": "text",
            key: "K01005_KOSZ",
            description: "Leírás",
            text: thankYouText,
            alignment: "CENTER",
            orderId: 1
        }, ...buildMontaxExtraCustomInfo(tableMate, seqnr)],
        paymentDetails: buildMontaxPaymentDetails(settings, types, payable),
        createDownloadInfo: true
    };

    const closeHttpPath = resolveSetting(settings, ["montaxCloseHttpPath", "closeHttpPath"], "/doc/doc-close");
    return {
        payload: JSON.stringify(body),
        requestOptions: {
            httpPath: closeHttpPath,
            httpMethod: "POST",
            appendNewLine: false,
            injectSystemIdFromDevice: true,
            headers: {
                "Content-Type": "application/json"
            }
        }
    };
};

export const generate_montax = (def, amounts, cashierType, payable, types, amountPaidExchangeRate, serviceVat, tip, tiptype, tableMate, seqnr) => {
    const register = resolveMontaxCashRegister(cashierType);
    const resolvedCashier = register || cashierType;
    const payload = buildMontaxReceiptPayload(resolvedCashier, amounts);
    const requestOptions = buildRequestOptions(resolvedCashier, ["montaxHttpPath", "httpPath"], ["montaxHttpMethod", "httpMethod"]);
    // First message (createReceipt) stays at the top level, the second one (closeReceipt) is sent by the server after the first succeeded.
    def.resolve(JSON.stringify({
        payload,
        requestOptions,
        closeMessage: buildMontaxCloseReceiptMessage(resolvedCashier, types, payable, tableMate, seqnr)
    }));
};

export const dayOpen_montax = (amount = 0, cashierType) => {
    const fallback = buildDefaultDayOpenPayload(cashierType, amount);
    const text = buildPayloadFromSettings(cashierType, ["montaxDayOpenPayload", "dayOpenPayload"], fallback, amount);
    return {
        text,
        pattern: buildPattern(cashierType),
        requestOptions: buildRequestOptions(cashierType, ["montaxDayOpenHttpPath", "dayOpenHttpPath"], ["montaxDayOpenHttpMethod", "dayOpenHttpMethod"])
    };
};

export const dayClose_montax = (cashierType) => {
    const cashRegisterReportFallback = buildDefaultCashRegisterReportPayload(cashierType);
    const fallback = buildDefaultDayClosePayload(cashierType);
    const cashRegisterReportText = buildPayloadFromSettings(cashierType, ["montaxCashRegisterReportPayload", "cashRegisterReportPayload"], cashRegisterReportFallback, 0);
    const text = buildPayloadFromSettings(cashierType, ["montaxDayClosePayload", "dayClosePayload"], fallback, 0);
    const requestOptions = buildRequestOptions(cashierType, ["montaxDayCloseHttpPath", "dayCloseHttpPath"], ["montaxDayCloseHttpMethod", "dayCloseHttpMethod"]);
    return {
        text,
        pattern: buildPattern(cashierType),
        requestOptions,
        cashRegisterReport: {
            text: cashRegisterReportText,
            requestOptions
        }
    };
};

export const buildReprint_montax = (receiptType, cashierType, context = {}) => {
    const settings = parseCashRegisterSettings(cashierType);
    const prefix = receiptType === "dayClose" ? "DayClose" : receiptType === "receipt" ? "Receipt" : "DayOpen";
    const lowerPrefix = prefix.charAt(0).toLowerCase() + prefix.slice(1);

    const payloadTemplate = resolveSetting(settings, [
        `montax${prefix}ReprintPayload`,
        `${lowerPrefix}ReprintPayload`,
        "montaxReprintPayload",
        "reprintPayload"
    ], "");

    const configuredPath = resolveSetting(settings, [
        `montax${prefix}ReprintHttpPath`,
        `${lowerPrefix}ReprintHttpPath`,
        "montaxReprintHttpPath",
        "reprintHttpPath"
    ], "");

    const configuredMethod = resolveSetting(settings, [
        `montax${prefix}ReprintHttpMethod`,
        `${lowerPrefix}ReprintHttpMethod`,
        "montaxReprintHttpMethod",
        "reprintHttpMethod"
    ], "");

    const configuredDocumentType = resolveSetting(settings, [
        `montax${prefix}ReprintDocumentType`,
        `${lowerPrefix}ReprintDocumentType`,
        `montax${prefix}DocumentType`,
        `${lowerPrefix}DocumentType`
    ], "");

    const defaultDocumentType = receiptType === "dayClose" ? "FISCAL_DAY_CLOSE" : "FISCAL_DAY_OPEN";
    if (!context.documentType && configuredDocumentType) {
        context.documentType = configuredDocumentType;
    }
    if (!context.documentType && context.forceDefaultDocumentType) {
        context.documentType = defaultDocumentType;
    }

    const generatedDocumentPath = buildDocumentDataPath(context, receiptType);

    const pathFromContext = context.httpPath || context.downloadPath || generatedDocumentPath || getPathFromUrl(context.downloadUrl);
    const path = templateValue(configuredPath || pathFromContext || "", context);
    const normalizedPath = path.startsWith("http://") || path.startsWith("https://") ? (getPathFromUrl(path) || path) : path;
    const httpMethod = String(configuredMethod || context.httpMethod || (generatedDocumentPath ? "GET" : "POST")).toUpperCase();

    let text = "";
    if (typeof payloadTemplate === "string" && payloadTemplate.trim()) {
        text = templateValue(payloadTemplate, context);
    } else if (typeof context.downloadPayload === "string") {
        text = context.downloadPayload;
    } else if (context.downloadPayload && typeof context.downloadPayload === "object") {
        text = JSON.stringify(context.downloadPayload);
    }

    return {
        text,
        pattern: buildPattern(cashierType),
        requestOptions: {
            httpPath: normalizedPath,
            httpMethod,
            appendNewLine: false,
            injectSystemIdFromDevice: true,
            injectSystemIdInPath: true,
            headers: {
                "Content-Type": "application/json",
                "Accept": "application/json"
            }
        }
    };
};

const MONTAX_RESULT_MESSAGES = {
    FISCAL_DAY_OPENED_ALREADY: "admin_local.cashier_day_already_opened",
    FISCAL_DAY_CLOSED: "admin_local.cashier_day_closed",
    NOT_ENOUGH_MONEY_IN_DRAWER: "admin_local.cashier_not_enough_money_in_drawer",
    CASH_REGISTER_REPORT_IS_NOT_CREATED: "admin_local.cashier_day_close_requires_cash_register_report",
    LATEST_DAY_CLOSE_DOCUMENTS_NOT_FOUND: "admin_local.cashier_day_close_history_empty"
};

const MONTAX_ALREADY_OPEN_RESULT_CODES = ["FISCAL_DAY_OPENED_ALREADY"];
const MONTAX_ALREADY_CLOSED_RESULT_CODES = [
    "FISCAL_DAY_CLOSED",
    "FISCAL_DAY_CLOSED_ALREADY",
    "DAY_CLOSED_ALREADY",
    "CASH_REGISTER_CLOSED_ALREADY",
    "CASH_REGISTER_IS_CLOSED"
];

export const parseMontaxResponse = (response) => {
    if (!response)
        return null;
    if (typeof response === "string") {
        try {
            const parsed = JSON.parse(response);
            if (Array.isArray(parsed))
                return parsed[0] || null;
            return parsed;
        } catch (e) {
            return null;
        }
    }
    if (Array.isArray(response))
        return response[0] || null;
    if (typeof response === "object")
        return response;
    return null;
};

export const getMontaxResultCode = (parsed) => {
    const montaxResponse = parsed?.responses?.[0] || parsed;
    return String(montaxResponse?.resultCode || "").toUpperCase();
};

const getMontaxResultDescription = (parsed) => {
    const montaxResponse = parsed?.responses?.[0] || parsed;
    return String(montaxResponse?.resultDesc || montaxResponse?.error || "");
};

const getMontaxLocalizedMessage = (parsed) => {
    const montaxResponse = parsed?.responses?.[0] || parsed;
    const resultCode = getMontaxResultCode(parsed);
    const translatedMessageKey = MONTAX_RESULT_MESSAGES[resultCode]
        || MONTAX_RESULT_MESSAGES[String(montaxResponse?.error || "").toUpperCase()];
    if (translatedMessageKey)
        return I18n.t(translatedMessageKey);
    return montaxResponse?.resultDesc || montaxResponse?.error || montaxResponse?.resultCode || null;
};

export const isMontaxAlreadyOpen = (parsed) => {
    const resultCode = getMontaxResultCode(parsed);
    if (MONTAX_ALREADY_OPEN_RESULT_CODES.includes(resultCode))
        return true;

    const resultDesc = getMontaxResultDescription(parsed);
    return /already\s+open(ed)?/i.test(resultDesc);
};

export const isMontaxAlreadyClosed = (parsed) => {
    const resultCode = getMontaxResultCode(parsed);
    if (MONTAX_ALREADY_CLOSED_RESULT_CODES.includes(resultCode))
        return true;
    if (resultCode.includes("CLOSED") && resultCode.includes("ALREADY"))
        return true;

    const resultDesc = getMontaxResultDescription(parsed);
    return /already\s+clos(ed|e)/i.test(resultDesc)
        || /clos(ed|e).+already/i.test(resultDesc)
        || /has\s+no\s+opened\s+fiscal\s+day/i.test(resultDesc)
        || /no\s+opened\s+fiscal\s+day/i.test(resultDesc);
};

export const getMontaxMessage = (parsed, fallbackMessage = null) => {
    const message = getMontaxLocalizedMessage(parsed);
    if (message)
        return message;
    return fallbackMessage || I18n.t("admin_local.kiosk_cash_register_communication_unsuccessful");
};

const normalizeMontaxPrinterText = (value) => String(value ?? "").replace(/\r?\n/g, " ");

const escapeMontaxPrinterHtml = (value) => String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

const getInvoicePrinter = () => getNonLocalPrinterForPrinterAction("printInvoice")
    || getNonLocalPrinterForPrinterAction("printVATInvoice")
    || admin.getPrinterForPrinterAction("printInvoice")
    || admin.getPrinterForPrinterAction("printVATInvoice")
    || null;

const getMontaxPrinterLineWidth = (printer) => {
    const rawWidth = printer?.width;
    if (Number.isFinite(rawWidth) && rawWidth > 0)
        return Math.max(16, Math.min(120, Number(rawWidth)));

    const widthText = String(rawWidth || "").trim();
    const parsedFromText = Number((widthText.match(/(\d+)/) || [])[1]);
    if (Number.isFinite(parsedFromText) && parsedFromText > 0)
        return Math.max(16, Math.min(120, parsedFromText));

    return 48;
};

const buildMontaxRightAlignedReportLine = (label, value, lineWidth) => {
    const left = String(label || "").trim();
    const right = String(value || "").trim();
    const width = Math.max(16, Number(lineWidth) || 48);

    if (!left && !right)
        return "!LF";
    if (!right)
        return `${left}!LF`;
    if (!left)
        return `${" ".repeat(Math.max(0, width - right.length))}${right}!LF`;

    const spaces = width - left.length - right.length;
    if (spaces >= 1)
        return `${left}${" ".repeat(spaces)}${right}!LF`;

    return `${left} ${right}!LF`;
};

// JavaPOS sequences (a raw "ESC ! n" is swallowed by the JavaPOS driver): ESC|1fT = font B (condensed),
// ESC|3C = double height, ESC|N = back to normal.
const MONTAX_ITEM_FONT = "!ESC|1fT!ESC|3C";
// The printer driver wraps lines by its own character count (it does not know about the font switch), so the
// text of an item line has to stay below the normal line width.
const MONTAX_ITEM_FONT_CONTROL_LENGTH = 0;
const MONTAX_NORMAL_FONT = "!ESC|N";

const formatMontaxAmount = (value) => {
    const text = String(value ?? "").trim();
    if (!/^-?\d+(\.\d+)?$/.test(text))
        return text;
    const [integerPart, decimalPart] = text.split(".");
    return `${integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, " ")}${decimalPart ? "," + decimalPart : ""}`;
};

const getMontaxAlignPrefix = (alignment) => {
    const value = String(alignment || "").toUpperCase();
    return value === "RIGHT" ? "!ESC|rA" : value === "CENTER" ? "!ESC|cA" : "";
};

const buildMontaxCenteredTitleLine = (title, lineWidth) => {
    const text = ` ${normalizeMontaxPrinterText(title).trim()} `;
    const dashes = Math.max(2, Math.floor((lineWidth - text.length) / 2));
    return `${"-".repeat(dashes)}${text}${"-".repeat(dashes)}!LF`;
};

const getMontaxQrData = (item) => {
    const type = String(item?.["@type"] || "");
    const textType = String(item?.textType || "");
    if (!/qr/i.test(type) && !/qr/i.test(textType) && typeof item?.qrCode === "undefined" && typeof item?.qrData === "undefined")
        return "";
    const candidates = [item.qrCode, item.qrData, item.data, item.value, item.text];
    const found = candidates.find(v => typeof v === "string" && v.trim());
    return found ? found.trim() : "";
};

// Fallback for document items that are not handled explicitly (header, totals, payment lines, QR code ...).
const buildMontaxGenericLine = (item, lineWidth, separatorLine) => {
    const qrData = getMontaxQrData(item);
    if (qrData)
        return `!ESCqrcode: ${qrData}!LF!LF`;

    const label = normalizeMontaxPrinterText(item.label ?? "");
    const value = normalizeMontaxPrinterText(typeof item.value === "object" && item.value ? (item.value.value ?? item.value.text ?? "") : (item.value ?? ""));
    const text = normalizeMontaxPrinterText(item.text ?? "");

    if (item.textType === "SEPARATOR")
        return label || text ? buildMontaxCenteredTitleLine(label || text, lineWidth) : separatorLine;
    if (label || (value && !text))
        return buildMontaxRightAlignedReportLine(label, value, lineWidth);
    if (text)
        return `${getMontaxAlignPrefix(item.alignment)}${text}!LF`;
    if (Array.isArray(item.lines))
        return item.lines.map(line => `${getMontaxAlignPrefix(item.alignment)}${normalizeMontaxPrinterText(line)}!LF`).join("");
    return "";
};

const formatMontaxAddress = (address) => {
    if (!address || typeof address !== "object")
        return "";
    const part = (value, suffix = "") => {
        const text = normalizeMontaxPrinterText(value ?? "").trim();
        return text ? (suffix ? `${text} ${suffix}` : text) : "";
    };
    return [
        part(address.postCode),
        part(address.city),
        part(address.additionalAddressDetail),
        part(address.streetName),
        part(address.publicPlaceCategory),
        part(address.houseNumber),
        part(address.building, "\u00c9P\u00dcLET"),
        part(address.staircase, "L\u00c9PCS\u0150H\u00c1Z"),
        part(address.floor),
        part(address.door),
        part(address.lotNumber),
    ].filter(Boolean).join(" ");
};

const wrapMontaxText = (text, width) => {
    const lines = [];
    let current = "";
    String(text || "").split(/\s+/).filter(Boolean).forEach(word => {
        if (current && (current.length + 1 + word.length) > width) {
            lines.push(current);
            current = word;
        } else {
            current = current ? `${current} ${word}` : word;
        }
    });
    if (current)
        lines.push(current);
    return lines;
};

// Company header of the receipt, taken from the docCreate item of the Montax response.
const buildMontaxReceiptHeader = (item, lineWidth) => {
    const site = item?.operatorSite;
    if (!site || typeof site !== "object")
        return "";
    const taxNumber = site.taxpayer?.taxNumber;
    const taxNumberText = taxNumber?.taxpayerId
        ? [taxNumber.taxpayerId, taxNumber.vatCode, taxNumber.countyCode].filter(v => v !== undefined && v !== null && v !== "").join("-")
        : "";
    const lines = [
        normalizeMontaxPrinterText(site.taxpayer?.taxpayerName || ""),
        formatMontaxAddress(site.taxpayer?.address),
        normalizeMontaxPrinterText(site.shop?.shopName || ""),
        formatMontaxAddress(site.shop?.address),
        taxNumberText ? `${normalizeMontaxPrinterText(item.taxNumberHeader || "")}${taxNumberText}` : "",
        normalizeMontaxPrinterText(item.headNote || ""),
    ];
    return lines
        .filter(line => line && line.trim())
        .map(line => wrapMontaxText(line, lineWidth).map(l => `!ESC|cA${l}!LF`).join(""))
        .join("");
};

// Summary block of the receipt (total, service fee, payments, change), taken from the docClose item of the Montax response.
const buildMontaxReceiptSummary = (item, lineWidth) => {
    const payments = Array.isArray(item?.paymentDetails) ? item.paymentDetails : [];
    const currency = normalizeMontaxPrinterText(payments.find(p => p?.currencySymbol)?.currencySymbol || "Ft");
    const labelOf = (text) => {
        const label = normalizeMontaxPrinterText(text || "").trim();
        return label && !label.endsWith(":") ? `${label}:` : label;
    };
    const amountOf = (value, symbol = currency) => {
        const text = formatMontaxAmount(normalizeMontaxPrinterText(value ?? "").trim());
        return text ? `${text} ${symbol}` : "";
    };
    const separatorLine = `${"-".repeat(lineWidth)}!LF`;

    const totalLines = [];
    if (item?.docTotal?.value !== undefined && item?.docTotal?.value !== null && String(item.docTotal.value).trim() !== "")
        totalLines.push(buildMontaxRightAlignedReportLine(labelOf(item.docTotal.text), amountOf(item.docTotal.value), lineWidth));
    if (item?.serviceFee?.value !== undefined && item?.serviceFee?.value !== null && String(item.serviceFee.value).trim() !== "")
        totalLines.push(buildMontaxRightAlignedReportLine(labelOf(item.serviceFee.text), amountOf(item.serviceFee.value), lineWidth));

    const paymentLines = payments
        .filter(p => p && (p.name || p.moneyAmount !== undefined))
        .map(p => buildMontaxRightAlignedReportLine(labelOf(p.name), amountOf(p.moneyAmount, normalizeMontaxPrinterText(p.currencySymbol || currency)), lineWidth));

    return `${totalLines.length ? separatorLine + totalLines.join("") : ""}${paymentLines.length ? separatorLine + paymentLines.join("") : ""}`;
};

const buildMontaxEscContent = (parsed, printer = null) => {
    const montaxResponse = parsed?.responses?.[0] || parsed;
    const documentData = Array.isArray(montaxResponse?.documentData) ? montaxResponse.documentData : [];
    if (!documentData.length)
        return null;

    const printerLineWidth = getMontaxPrinterLineWidth(printer);
    const separatorLine = `${"-".repeat(printerLineWidth)}!LF`;
    const isReceipt = documentData.some(item => item?.["@type"] === "receiptItem");

    // Control QR (e-receipt download info, NAV output QR), printed below the footer text and above the extra info.
    const downloadInfoEntry = [].concat(montaxResponse?.docDownloadInfo ?? []).find(Boolean);
    const downloadInfoQr = isReceipt && downloadInfoEntry
        ? (typeof downloadInfoEntry === "string" ? downloadInfoEntry : JSON.stringify(downloadInfoEntry)).trim()
        : "";

    const rows = documentData.map((item) => {
        if (!item || typeof item !== "object")
            return "";

        if (isReceipt && item["@type"] === "docCreate") {
            return `${buildMontaxReceiptHeader(item, printerLineWidth)}!LF`
                + (item.docTitle ? `!ESC|cA${buildMontaxCenteredTitleLine(item.docTitle, printerLineWidth)}` : "");
        }

        if (isReceipt && item["@type"] === "docClose") {
            const docNoText = normalizeMontaxPrinterText(item.docNo?.text || "");
            const docNoValue = normalizeMontaxPrinterText(item.docNo?.value || "");
            const closeDate = normalizeMontaxPrinterText(item.docCloseDate?.value || "");
            const dateMatch = closeDate.match(/^(.*?)[\sT]+(\d{1,2}:\d{2})(?::\d{2})?$/);
            const validationText = normalizeMontaxPrinterText(item.docValidationCode?.text || "");
            const validationValue = normalizeMontaxPrinterText(item.docValidationCode?.value || "");
            const systemId = String(item.systemId || parseMontaxDocNoParts(String(item.docNo?.value || "")).systemId || "").trim();
            const buildCustomInfoLines = (infos) => infos
                .map(info => {
                    if (info?.["@type"] === "barcodeQr" && typeof info.data === "string" && info.data.trim())
                        return `!LF!ESCqrcode: ${info.data.trim()}!LF!LF`;
                    return info?.text ? `${getMontaxAlignPrefix(info.alignment)}${normalizeMontaxPrinterText(info.text)}!LF` : "";
                })
                .join("");
            const allCustomInfo = (Array.isArray(item.docCustomInfo) ? [...item.docCustomInfo] : [])
                .sort((a, b) => (Number(a?.orderId) || 0) - (Number(b?.orderId) || 0));
            const footerInfo = allCustomInfo.filter(info => info?.key === "K01005_KOSZ");
            const extraInfo = allCustomInfo.filter(info => info?.key !== "K01005_KOSZ");
            const extraInfoText = buildCustomInfoLines(extraInfo);
            const downloadQrText = downloadInfoQr
                ? `!LF!ESC|cA${normalizeMontaxPrinterText("E-nyugta letöltése QR kóddal")}!LF!ESC|cA${normalizeMontaxPrinterText("Download your e-receipt with the QR code")}!LF!ESCqrcode: ${downloadInfoQr}!LF!LF`
                : "";
            const customInfoText = `${buildCustomInfoLines(footerInfo)}${downloadQrText}${extraInfoText ? "!LF!LF" + extraInfoText : ""}`;
            return `${buildMontaxReceiptSummary(item, printerLineWidth)}!LF${docNoValue ? buildMontaxRightAlignedReportLine(docNoText, docNoValue, printerLineWidth) : ""}`
                + `${closeDate ? (dateMatch ? buildMontaxRightAlignedReportLine(dateMatch[1], dateMatch[2], printerLineWidth) : closeDate + "!LF") : ""}`
                + `!LF${validationValue ? `!ESC|cA${(validationText + " " + validationValue).trim()}!LF` : ""}`
                + `${systemId ? `!ESC|cA\u00c1P ${normalizeMontaxPrinterText(systemId)}!LF` : ""}`
                + `${(() => {
                    const qr = [item.qrCode, item.qrData, item.docQrCode?.value, item.docQrCode].find(v => typeof v === "string" && v.trim());
                    return qr ? `!LF!ESCqrcode: ${qr.trim()}!LF!LF` : "";
                })()}`
                + `${customInfoText}`;
        }

        if (item["@type"] === "docCreate") {
            return `${item.docTitle ? "!ESC|cA!ESC|bC" + normalizeMontaxPrinterText(item.docTitle) + "!LF" : ""}${item.docCreationDate ? "!ESC|cA" + normalizeMontaxPrinterText(item.docCreationDate) + "!LF" : ""}`;
        }

        if (item["@type"] === "reportItem") {
            if (item.textType === "SEPARATOR") {
                return separatorLine;
            }
            const label = normalizeMontaxPrinterText(item.label || "");
            const value = normalizeMontaxPrinterText(item.value || "");
            return buildMontaxRightAlignedReportLine(label, value, printerLineWidth);
        }

        if (item["@type"] === "receiptItem") {
            const name = normalizeMontaxPrinterText(item.itemName || "");
            const qty = String(item.itemQty ?? "").trim();
            const unitPrice = String(item.itemUnitPrice ?? "").trim();
            const total = String(item.itemSum ?? item.itemTotalPrice ?? item.itemTotal ?? item.itemGrossPrice ?? item.itemAmount ?? "").trim();
            const detail = [qty, unitPrice].filter(Boolean).join(" x ");
            const dept = String(item.itemDept ?? "").trim();
            const right = `${formatMontaxAmount(total)}${dept ? " " + dept : ""}`;
            const detailLine = qty && Number(qty) !== 1 ? `  ${detail}!LF` : "";
            const itemLineWidth = Math.max(24, printerLineWidth - MONTAX_ITEM_FONT_CONTROL_LENGTH);
            return `${buildMontaxRightAlignedReportLine(name.slice(0, Math.max(1, itemLineWidth - right.length - 1)), right, itemLineWidth)}${detailLine}`;
        }

        if (item["@type"] === "docClose") {
            const customInfo = Array.isArray(item.docCustomInfo) ? item.docCustomInfo : [];
            const customInfoText = customInfo
                .map(info => {
                    if (!info?.text)
                        return "";
                    const alignment = String(info.alignment || "").toUpperCase();
                    const alignPrefix = alignment === "RIGHT" ? "!ESC|rA" : alignment === "CENTER" ? "!ESC|cA" : "";
                    return `${alignPrefix}${normalizeMontaxPrinterText(info.text)}!LF`;
                })
                .join("");
            return `!LF!LF!ESC|cA${normalizeMontaxPrinterText(item.docNo?.text || "")} ${normalizeMontaxPrinterText(item.docNo?.value || "")}!LF!ESC|cA${normalizeMontaxPrinterText(item.docValidationCode?.text || "")} ${normalizeMontaxPrinterText(item.docValidationCode?.value || "")}!LF!ESC|cA${normalizeMontaxPrinterText(item.docCloseDate?.text || "")} ${normalizeMontaxPrinterText(item.docCloseDate?.value || "")}!LF${customInfoText}`;
        }

        if (isReceipt)
            return buildMontaxGenericLine(item, printerLineWidth, separatorLine);

        return "";
    }).join("");

    // Receipts: every text line (header, items, footer) uses the same condensed font; blank lines and the QR code stay as they are.
    const styledRows = isReceipt
        ? rows.split("!LF")
            .map(line => line.trim() && !line.includes("!ESCqrcode:") ? `${MONTAX_ITEM_FONT}${line}${MONTAX_NORMAL_FONT}` : line)
            .join("!LF")
        : rows;

    return `${styledRows}!LF!LF!LF`;
};

const hasMontaxPrintableData = (parsed) => {
    const montaxResponse = parsed?.responses?.[0] || parsed;
    return Array.isArray(montaxResponse?.documentData) && montaxResponse.documentData.length > 0;
};

const getMontaxReprintCacheKey = (cashRegister, type) => {
    const restaurant = localStorage.restaurantSelected || "";
    const registerKey = cashRegister?.id || cashRegister?.name || "unknown";
    return `montax-reprint-context:${restaurant}:${registerKey}:${type}`;
};

export const saveMontaxReprintContext = (cashRegister, type, context) => {
    try {
        if (!context || typeof context !== "object")
            return;
        localStorage.setItem(getMontaxReprintCacheKey(cashRegister, type), JSON.stringify(context));
    } catch (e) {
    }
};

const loadMontaxReprintContext = (cashRegister, type) => {
    try {
        const raw = localStorage.getItem(getMontaxReprintCacheKey(cashRegister, type));
        if (!raw)
            return null;
        const parsed = JSON.parse(raw);
        return parsed && typeof parsed === "object" ? parsed : null;
    } catch (e) {
        return null;
    }
};

const getMontaxSystemIdForSearch = (cashRegister, responseContext) => {
    const fromResponse = String(responseContext?.systemId || "").trim();
    if (fromResponse)
        return fromResponse;
    const fromSettings = String(resolveSetting(parseCashRegisterSettings(cashRegister), ["montaxSystemId", "systemId", "ap"], "")).trim();
    if (fromSettings)
        return fromSettings;
    return "{systemId}";
};

const getMontaxListTypeCandidates = (cashRegister, type) => {
    const settings = parseCashRegisterSettings(cashRegister);
    const configured = String(resolveSetting(settings,
        type === "dayClose"
            ? ["montaxDayCloseListDocumentTypes", "dayCloseListDocumentTypes"]
            : ["montaxDayOpenListDocumentTypes", "dayOpenListDocumentTypes"], "")).trim();

    if (configured) {
        return configured.split(",").map(v => v.trim()).filter(Boolean);
    }

    if (type === "dayClose")
        return ["CASH_REGISTER_REPORT", "FISCAL_DAY_REPORT", "FISCAL_DAY_CLOSE"];
    if (type === "receipt")
        return ["RECEIPT"];
    return ["FISCAL_DAY_OPEN"];
};

const uniqueList = (values) => Array.from(new Set((values || []).map(v => String(v || "").trim()).filter(Boolean)));

const buildMontaxListRequestOptions = (httpPath) => ({
    httpPath,
    httpMethod: "GET",
    appendNewLine: false,
    injectSystemIdFromDevice: false,
    injectSystemIdInPath: true,
    headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
    }
});

const fetchMontaxLatestDocumentDescriptor = (cashRegister, type, responseContext, sendToCashier, explicitCandidates = null, strictDocumentType = false) => {
    const def = $.Deferred();
    const systemId = getMontaxSystemIdForSearch(cashRegister, responseContext);
    const settings = parseCashRegisterSettings(cashRegister);

    const searchDaysRaw = Number(resolveSetting(settings, ["montaxReprintSearchDays", "reprintSearchDays"], 7));
    const searchDays = Number.isFinite(searchDaysRaw) && searchDaysRaw > 0 ? searchDaysRaw : 7;
    const endTime = Date.now();
    const startTime = endTime - searchDays * 24 * 60 * 60 * 1000;

    const candidates = uniqueList(explicitCandidates || getMontaxListTypeCandidates(cashRegister, type));
    const encodedSystemId = systemId === "{systemId}" ? systemId : encodeURIComponent(systemId);
    const paths = candidates.map(documentType => `/doc/list/${encodedSystemId}/${startTime}/${endTime}?documentType=${encodeURIComponent(documentType)}`);
    paths.push(`/doc/list/${encodedSystemId}/${startTime}/${endTime}`);

    const rankDocument = (doc) => {
        const timestamp = Number(doc?.docCreationDate || 0);
        const docId = Number(doc?.docId || 0);
        return timestamp * 1000000 + docId;
    };

    const isTypeMatch = (docType) => {
        const t = String(docType || "").toUpperCase();
        if (!t)
            return false;
        if (candidates.find(c => t === String(c).toUpperCase()))
            return true;
        if (strictDocumentType)
            return false;
        if (type === "receipt")
            return t.includes("RECEIPT");
        if (type === "dayOpen")
            return t.includes("OPEN");
        return t.includes("CLOSE") || t.includes("REPORT");
    };

    const tryPath = (index) => {
        if (index >= paths.length) {
            def.reject({ error: "LATEST_DOCUMENT_NOT_FOUND", resultDesc: `No ${type} document found in the last ${searchDays} days.` });
            return;
        }

        const path = paths[index];
        sendToCashier(cashRegister, "", ".*", false, (response) => {
            const parsed = parseMontaxResponse(response);
            if (!parsed || getMontaxResultCode(parsed) !== "SUCCESS") {
                tryPath(index + 1);
                return;
            }

            const montaxResponse = parsed?.responses?.[0] || parsed;
            const documents = Array.isArray(montaxResponse?.documents) ? montaxResponse.documents : [];
            const filtered = documents.filter(doc => isTypeMatch(doc?.type));
            if (!filtered.length) {
                tryPath(index + 1);
                return;
            }

            filtered.sort((a, b) => rankDocument(b) - rankDocument(a));
            const latest = filtered[0];
            if (!latest || typeof latest.fiscalDayNo === "undefined" || typeof latest.docId === "undefined") {
                tryPath(index + 1);
                return;
            }

            def.resolve({
                systemId,
                fiscalDayNo: String(latest.fiscalDayNo),
                documentType: String(latest.type || candidates[0] || ""),
                documentId: String(latest.docId),
                formatType: "FORMATTED"
            });
        }, () => {
            tryPath(index + 1);
        }, buildMontaxListRequestOptions(path));
    };

    tryPath(0);
    return def.promise();
};

const fetchMontaxLatestDocumentDescriptorsForDayClose = (cashRegister, responseContext, sendToCashier) => {
    const def = $.Deferred();
    const configuredCandidates = uniqueList(getMontaxListTypeCandidates(cashRegister, "dayClose"));
    const preferredCandidates = uniqueList([
        "CASH_REGISTER_REPORT",
        "FISCAL_DAY_REPORT",
        ...configuredCandidates
    ]);
    const descriptors = [];

    const mustHaveCandidates = ["CASH_REGISTER_REPORT", "FISCAL_DAY_REPORT"];
    const fetchCandidate = (candidate) => fetchMontaxLatestDocumentDescriptor(cashRegister, "dayClose", responseContext, sendToCashier, [candidate], true);

    fetchCandidate(mustHaveCandidates[0]).done((first) => {
        descriptors.push(first);
        fetchCandidate(mustHaveCandidates[1]).done((second) => {
            descriptors.push(second);
            def.resolve(descriptors);
        }).fail(() => {
            def.reject({
                error: "LATEST_DAY_CLOSE_DOCUMENTS_NOT_FOUND",
                resultDesc: "Could not find fiscal day close report for reprint."
            });
        });
    }).fail(() => {
        const fallback = [];
        const fetchNextFallback = (index) => {
            if (index >= preferredCandidates.length) {
                if (fallback.length >= 2) {
                    def.resolve(fallback.slice(0, 2));
                    return;
                }
                def.reject({
                    error: "LATEST_DAY_CLOSE_DOCUMENTS_NOT_FOUND",
                    resultDesc: "Could not find both day-close receipts (cash register report and fiscal day report)."
                });
                return;
            }
            const candidate = preferredCandidates[index];
            fetchCandidate(candidate).done((descriptor) => {
                if (!fallback.find(d => String(d.documentType || "").toUpperCase() === String(descriptor.documentType || "").toUpperCase())) {
                    fallback.push(descriptor);
                }
                fetchNextFallback(index + 1);
            }).fail(() => {
                fetchNextFallback(index + 1);
            });
        };
        fetchNextFallback(0);
    });

    return def.promise();
};

const decodeMontaxPathToken = (value) => {
    if (typeof value !== "string")
        return "";
    try {
        return decodeURIComponent(value);
    } catch (e) {
        return value;
    }
};

const extractMontaxDocumentPathInfo = (value) => {
    if (typeof value !== "string" || !value.trim())
        return null;

    let source = value.trim();
    if (source.startsWith("http://") || source.startsWith("https://")) {
        try {
            source = `${new URL(source).pathname}${new URL(source).search}`;
        } catch (e) {
        }
    }

    const match = source.match(/\/doc\/(?:document|document-image)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/]+)\/([^/?#]+)/i);
    if (!match)
        return null;

    return {
        systemId: decodeMontaxPathToken(match[1]),
        fiscalDayNo: decodeMontaxPathToken(match[2]),
        documentType: decodeMontaxPathToken(match[3]),
        documentId: decodeMontaxPathToken(match[4]),
        formatType: decodeMontaxPathToken(match[5])
    };
};

const parseMontaxDocNoParts = (docNo) => {
    if (typeof docNo !== "string" || !docNo.trim())
        return {};
    const clean = docNo.trim();
    const parts = clean.split("/");
    if (parts.length < 2)
        return {};

    const systemCandidate = (parts[0].split("-").pop() || "").trim();
    const fiscalDayNo = (parts[1] || "").trim();
    const documentId = (parts[parts.length - 1] || "").trim();

    return {
        systemId: systemCandidate,
        fiscalDayNo,
        documentId
    };
};

export const getMontaxReprintContext = (parsed) => {
    const montaxResponse = parsed?.responses?.[0] || parsed || {};
    const resultDescText = String(montaxResponse?.resultDesc || "");
    const documentData = Array.isArray(montaxResponse?.documentData) ? montaxResponse.documentData : [];
    const docCloseItem = documentData.find(item => item && item["@type"] === "docClose") || {};
    const docNoFromResultDesc = (() => {
        const match = resultDescText.match(/([A-Z]{1,4}-[A-Z0-9]+\/[0-9]+\/[0-9]+\/[0-9]+)/i);
        return match ? String(match[1]).trim() : "";
    })();
    const docNoValue = String(docCloseItem?.docNo?.value || montaxResponse?.docNo || montaxResponse?.documentNo || docNoFromResultDesc || "");
    const docNoParts = parseMontaxDocNoParts(docNoValue);

    const docDownloadInfoEntries = [];
    const pushDocDownloadEntry = (entry) => {
        if (!entry)
            return;
        if (Array.isArray(entry)) {
            entry.forEach(pushDocDownloadEntry);
            return;
        }
        if (typeof entry === "string") {
            const trimmed = entry.trim();
            if (trimmed)
                docDownloadInfoEntries.push(trimmed);
            return;
        }
        if (typeof entry === "object") {
            docDownloadInfoEntries.push(entry);
            Object.keys(entry).forEach((key) => {
                const val = entry[key];
                if (typeof val === "string" && (val.includes("/doc/document/") || val.includes("/doc/document-image/"))) {
                    docDownloadInfoEntries.push(val);
                }
            });
        }
    };

    pushDocDownloadEntry(montaxResponse?.docDownloadInfo);
    pushDocDownloadEntry(resultDescText);

    const pathInfoFromDocDownload = (() => {
        for (let i = 0; i < docDownloadInfoEntries.length; i++) {
            const entry = docDownloadInfoEntries[i];
            if (typeof entry === "string") {
                const parsedPath = extractMontaxDocumentPathInfo(entry);
                if (parsedPath)
                    return parsedPath;
            } else if (entry && typeof entry === "object") {
                const direct = extractMontaxDocumentPathInfo(String(entry.url || entry.downloadUrl || entry.path || entry.httpPath || ""));
                if (direct)
                    return direct;
            }
        }
        return null;
    })();

    const sourceCandidates = [
        montaxResponse?.downloadInfo,
        montaxResponse?.docDownloadInfo,
        montaxResponse?.receiptDownloadInfo,
        montaxResponse?.download,
        montaxResponse
    ].filter(Boolean);

    const pickFirst = (keys) => {
        for (let i = 0; i < sourceCandidates.length; i++) {
            const source = sourceCandidates[i];
            if (!source || typeof source !== "object")
                continue;
            for (let j = 0; j < keys.length; j++) {
                const value = source[keys[j]];
                if (typeof value === "string" && value.trim())
                    return value.trim();
            }
        }
        return "";
    };

    const pickPayload = () => {
        for (let i = 0; i < sourceCandidates.length; i++) {
            const source = sourceCandidates[i];
            if (!source || typeof source !== "object")
                continue;
            const payload = source.downloadPayload || source.requestBody || source.payload || source.body;
            if (typeof payload === "string" && payload.trim())
                return payload;
            if (payload && typeof payload === "object")
                return payload;
        }
        return null;
    };

    return {
        downloadUrl: pickFirst(["downloadUrl", "url"]),
        downloadPath: pickFirst(["downloadPath", "path", "httpPath", "endpoint"]) || String(pathInfoFromDocDownload?.path || ""),
        httpMethod: pickFirst(["httpMethod", "method"]),
        downloadPayload: pickPayload(),
        docNo: docNoValue,
        documentNo: docNoValue,
        systemId: String(pathInfoFromDocDownload?.systemId || docCloseItem?.systemId || montaxResponse?.systemId || docNoParts.systemId || ""),
        fiscalDayNo: String(pathInfoFromDocDownload?.fiscalDayNo || montaxResponse?.fiscalDayNo || docNoParts.fiscalDayNo || ""),
        documentType: String(pathInfoFromDocDownload?.documentType || montaxResponse?.documentType || ""),
        documentId: String(pathInfoFromDocDownload?.documentId || montaxResponse?.documentId || docNoParts.documentId || ""),
        formatType: String(pathInfoFromDocDownload?.formatType || "FORMATTED"),
        validationCode: String(docCloseItem?.docValidationCode?.value || montaxResponse?.validationCode || ""),
        resultCode: getMontaxResultCode(parsed)
    };
};

export const mergeMontaxReprintContexts = (primary, fallback) => {
    const p = primary || {};
    const f = fallback || {};
    return {
        ...f,
        ...p,
        downloadUrl: p.downloadUrl || f.downloadUrl || "",
        downloadPath: p.downloadPath || f.downloadPath || "",
        httpMethod: p.httpMethod || f.httpMethod || "",
        downloadPayload: p.downloadPayload || f.downloadPayload || null,
        docNo: p.docNo || f.docNo || "",
        documentNo: p.documentNo || f.documentNo || "",
        systemId: p.systemId || f.systemId || "",
        fiscalDayNo: p.fiscalDayNo || f.fiscalDayNo || "",
        documentType: p.documentType || f.documentType || "",
        documentId: p.documentId || f.documentId || "",
        formatType: p.formatType || f.formatType || "FORMATTED",
        validationCode: p.validationCode || f.validationCode || "",
        resultCode: p.resultCode || f.resultCode || ""
    };
};

export const printMontaxDocument = (parsed) => {
    const printer = getInvoicePrinter();
    const printerName = printer?.name;
    if (!printerName)
        return false;
    const escContent = buildMontaxEscContent(parsed, printer);
    if (!escContent)
        return false;
    const printableHtml = `<html><body><pre style="margin:0;white-space:pre">${escapeMontaxPrinterHtml(escContent)}</pre></body></html>`;
    const url = `printerServerClient/${localStorage.restaurantSelected}/print/${encodeURIComponent(printerName)}?printer=${encodeURIComponent(printerName)}&productionLine=0&orderIds=montax-report-${Math.random() + new Date().getTime()}&reprint=false`;
    post(url, printableHtml, {
        Accept: "application/json",
        "Content-Type": "text/html; charset=utf-8"
    }, undefined, 2, undefined, 60000);
    return true;
};

// Downloads the given documents one after the other. saveContext keeps the latest downloaded document as the cached reprint context.
const downloadMontaxDocuments = (cashRegister, contexts, baseContext, type, sendToCashier, saveContext = true) => {
    const def = $.Deferred();
    const results = [];

    const requestNext = (index) => {
        if (index >= contexts.length) {
            if (!results.length) {
                def.reject({ error: "EMPTY_REPRINT_DOCUMENT" });
                return;
            }
            def.resolve(results);
            return;
        }

        const context = {
            ...baseContext,
            ...contexts[index],
            forceDefaultDocumentType: false
        };
        const reprintRequest = buildReprint_montax(type, cashRegister, context);
        const httpPath = reprintRequest?.requestOptions?.httpPath;
        if (!httpPath || !String(httpPath).trim()) {
            def.reject({
                error: "MISSING_REPRINT_PATH",
                resultDesc: `Missing Montax re-download path. Required fields: systemId=${context.systemId || ""}, fiscalDayNo=${context.fiscalDayNo || ""}, documentType=${context.documentType || ""}, documentId=${context.documentId || ""}.`
            });
            return;
        }

        sendToCashier(cashRegister, reprintRequest.text || "", reprintRequest.pattern, false, (response) => {
            const parsed = parseMontaxResponse(response);
            if (!parsed) {
                def.reject({ error: "INVALID_REPRINT_RESPONSE" });
                return;
            }
            const resultCode = getMontaxResultCode(parsed);
            if (resultCode && resultCode !== "SUCCESS") {
                def.reject(parsed);
                return;
            }
            if (!hasMontaxPrintableData(parsed)) {
                def.reject({ error: "EMPTY_REPRINT_DOCUMENT" });
                return;
            }
            if (saveContext)
                saveMontaxReprintContext(cashRegister, type, mergeMontaxReprintContexts(getMontaxReprintContext(parsed), context));
            results.push(parsed);
            requestNext(index + 1);
        }, (response) => {
            const parsed = parseMontaxResponse(response);
            def.reject(parsed || { error: "REPRINT_REQUEST_FAILED" });
        }, reprintRequest.requestOptions);
    };

    requestNext(0);
    return def.promise();
};

const requestMontaxReprintDocuments = (cashRegister, responseParsed, type, sendToCashier) => {
    const def = $.Deferred();
    const responseContext = getMontaxReprintContext(responseParsed);
    const cachedContext = loadMontaxReprintContext(cashRegister, type);
    const mergedContext = mergeMontaxReprintContexts(responseContext, cachedContext);

    const fetchDescriptorsPromise = type === "dayClose"
        ? fetchMontaxLatestDocumentDescriptorsForDayClose(cashRegister, mergedContext, sendToCashier)
        : fetchMontaxLatestDocumentDescriptor(cashRegister, type, mergedContext, sendToCashier).then((descriptor) => [descriptor]);

    fetchDescriptorsPromise.done((latestContexts) => {
        const contexts = Array.isArray(latestContexts) ? latestContexts : [latestContexts];
        downloadMontaxDocuments(cashRegister, contexts, mergedContext, type, sendToCashier, true)
            .done((results) => def.resolve(results))
            .fail((error) => def.reject(error));
    }).fail((lookupError) => {
        def.reject(lookupError || { error: "REPRINT_LOOKUP_FAILED" });
    });

    return def.promise();
};

export const confirmMontaxDownloadAndPrint = (cashRegister, parsed, type, sendToCashier) => {
    const messageKey = type === "dayClose"
        ? "admin_local.cashier_day_close_reprint_receipt_question"
        : "admin_local.cashier_day_open_reprint_receipt_question";

    confirmDialog(I18n.t("local.confirmation"), I18n.t(messageKey)).done(() => {
        requestMontaxReprintDocuments(cashRegister, parsed, type, sendToCashier).done((reprintParsedList) => {
            const documents = Array.isArray(reprintParsedList) ? reprintParsedList : [reprintParsedList];
            let anyPrinted = false;
            documents.forEach((docParsed) => {
                const printed = printMontaxDocument(docParsed);
                anyPrinted = anyPrinted || printed;
            });
            if (!anyPrinted) {
                messageDialog(I18n.t("local.warning"), I18n.t("admin_local.cashier_day_reprint_download_failed"));
            }
        }).fail((downloadError) => {
            const parsedDownloadError = parseMontaxResponse(downloadError);
            const message = getMontaxMessage(parsedDownloadError, I18n.t("admin_local.cashier_day_reprint_download_failed"));
            messageDialog(I18n.t("local.warning"), message);
        });
    });
};

// Downloads the latest receipt from Montax and prints it the same way as the day open/close documents.
export const printMontaxReceipt = (cashRegister, sendToCashier) => {
    requestMontaxReprintDocuments(cashRegister, null, "receipt", sendToCashier).done((documents) => {
        const printed = (Array.isArray(documents) ? documents : [documents]).map(printMontaxDocument).some(Boolean);
        if (!printed) {
            messageDialog(I18n.t("local.warning"), I18n.t("admin_local.cashier_day_reprint_download_failed"));
        }
    }).fail((downloadError) => {
        const message = getMontaxMessage(parseMontaxResponse(downloadError), I18n.t("admin_local.cashier_day_reprint_download_failed"));
        messageDialog(I18n.t("local.warning"), message);
    });
};

const MONTAX_DAY_CLOSE_REPORT_TYPES = ["CASH_REGISTER_REPORT", "FISCAL_DAY_REPORT"];
const MONTAX_DAY_CLOSE_HISTORY_MAX_PERIOD_DAYS = 31;

// Lists the closed fiscal days (newest first) with their day closure report documents in the given period.
const listMontaxDayCloseDays = (cashRegister, sendToCashier, startTime, endTime) => {
    const def = $.Deferred();
    const systemId = getMontaxSystemIdForSearch(cashRegister, null);
    const encodedSystemId = systemId === "{systemId}" ? systemId : encodeURIComponent(systemId);
    const days = {};

    const fetchType = (index) => {
        if (index >= MONTAX_DAY_CLOSE_REPORT_TYPES.length) {
            const list = Object.values(days)
                .map(day => ({
                    ...day,
                    documents: MONTAX_DAY_CLOSE_REPORT_TYPES.map(t => day.byType[t]).filter(Boolean)
                }))
                .sort((a, b) => Number(b.fiscalDayNo) - Number(a.fiscalDayNo));
            if (!list.length) {
                def.reject({ error: "LATEST_DAY_CLOSE_DOCUMENTS_NOT_FOUND" });
                return;
            }
            def.resolve(list);
            return;
        }

        const documentType = MONTAX_DAY_CLOSE_REPORT_TYPES[index];
        const path = `/doc/list/${encodedSystemId}/${startTime}/${endTime}?documentType=${encodeURIComponent(documentType)}`;
        sendToCashier(cashRegister, "", ".*", false, (response) => {
            const parsed = parseMontaxResponse(response);
            if (parsed && getMontaxResultCode(parsed) === "SUCCESS") {
                const montaxResponse = parsed?.responses?.[0] || parsed;
                const documents = Array.isArray(montaxResponse?.documents) ? montaxResponse.documents : [];
                documents.forEach((doc) => {
                    if (String(doc?.type || "").toUpperCase() !== documentType
                        || typeof doc.fiscalDayNo === "undefined" || typeof doc.docId === "undefined") {
                        return;
                    }
                    const key = String(doc.fiscalDayNo);
                    const day = days[key] || (days[key] = { fiscalDayNo: key, date: 0, byType: {} });
                    day.date = Math.max(day.date, Number(doc.docCreationDate || 0));
                    const existing = day.byType[documentType];
                    if (!existing || Number(doc.docId) > Number(existing.documentId)) {
                        day.byType[documentType] = {
                            systemId,
                            fiscalDayNo: key,
                            documentType,
                            documentId: String(doc.docId),
                            formatType: "FORMATTED"
                        };
                    }
                });
            }
            fetchType(index + 1);
        }, () => {
            fetchType(index + 1);
        }, buildMontaxListRequestOptions(path));
    };

    fetchType(0);
    return def.promise();
};

// Lets the user pick an old closed fiscal day and prints its day closure reports again.
export const reprintMontaxDayClose = (cashRegister, sendToCashier) => {
    const showFailure = (error, fallbackKey) => {
        const message = getMontaxMessage(parseMontaxResponse(error), I18n.t(fallbackKey));
        messageDialog(I18n.t("local.warning"), message);
    };

    // The dialog is removed from the DOM as soon as it resolves, so the field values are tracked while it is open.
    const askValues = (title, message, initialValues, done) => {
        const values = { ...initialValues };
        const selector = Object.keys(initialValues).map(id => `#${id}`).join(",");
        const onChange = (event) => {
            values[event.target.id] = $(event.target).val();
        };
        $(document).on("input.montaxDayCloseDialog change.montaxDayCloseDialog", selector, onChange);
        choiceDialog2(title, message, ["local.ok"]).done(() => done(values)).always(() => {
            $(document).off(".montaxDayCloseDialog");
        });
    };

    const formatDateInput = (time) => {
        const d = new Date(time);
        const pad = (n) => String(n).padStart(2, "0");
        return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
    };

    const showDays = (days) => {
        const options = days.map((day, index) => {
            const date = day.date ? new Date(day.date).toLocaleString() : "";
            return `<option value="${index}">${day.fiscalDayNo}${date ? " - " + date : ""}</option>`;
        }).join("");
        const message = `${I18n.t("admin_local.cashier_day_close_history_select")}<br/><select id="montaxDayCloseHistorySelect" class="form-control" style="font-size:inherit;margin-top:15px">${options}</select>`;

        askValues(I18n.t("admin_local.cashier_day_close_history_title"), message, { montaxDayCloseHistorySelect: "0" }, (values) => {
            const day = days[Number(values.montaxDayCloseHistorySelect)];
            if (!day) {
                return;
            }
            downloadMontaxDocuments(cashRegister, day.documents, {}, "dayClose", sendToCashier, false).done((documents) => {
                const printed = documents.map(printMontaxDocument).some(Boolean);
                if (!printed) {
                    messageDialog(I18n.t("local.warning"), I18n.t("admin_local.cashier_day_reprint_download_failed"));
                }
            }).fail((error) => {
                showFailure(error, "admin_local.cashier_day_reprint_download_failed");
            });
        });
    };

    const dayMs = 24 * 60 * 60 * 1000;
    const yesterday = formatDateInput(Date.now() - dayMs);
    const inputStyle = "font-size:inherit;margin-top:10px";
    const periodMessage = `${I18n.t("admin_local.cashier_day_close_history_period")}`
        + `<br/>${I18n.t("admin_local.cashier_day_close_history_from")}<input type="date" id="montaxDayCloseFrom" class="form-control" style="${inputStyle}" value="${yesterday}"/>`
        + `<br/>${I18n.t("admin_local.cashier_day_close_history_to")}<input type="date" id="montaxDayCloseTo" class="form-control" style="${inputStyle}" value="${yesterday}"/>`;

    askValues(I18n.t("admin_local.cashier_day_close_history_title"), periodMessage, {
        montaxDayCloseFrom: yesterday,
        montaxDayCloseTo: yesterday
    }, (values) => {
        const startTime = new Date(`${values.montaxDayCloseFrom}T00:00:00`).getTime();
        const endTime = Math.min(new Date(`${values.montaxDayCloseTo}T23:59:59`).getTime(), Date.now());
        if (!Number.isFinite(startTime) || !Number.isFinite(endTime) || startTime >= endTime
            || endTime - startTime > MONTAX_DAY_CLOSE_HISTORY_MAX_PERIOD_DAYS * dayMs) {
            messageDialog(I18n.t("local.warning"), I18n.t("admin_local.cashier_day_close_history_period_invalid"));
            return;
        }
        listMontaxDayCloseDays(cashRegister, sendToCashier, startTime, endTime).done(showDays).fail((error) => {
            showFailure(error, "admin_local.cashier_day_close_history_empty");
        });
    });
};

// Downloads a stored receipt (receiptNo format: fiscalDayNo/documentId) from Montax and prints it again.
// documentType is RECEIPT for sales and VOID_RECEIPT for storno receipts.
export const reprintMontaxReceipt = (cashRegister, receiptNo, sendToCashier, documentType = "RECEIPT") => {
    const showFailure = (parsedError) => {
        const message = getMontaxMessage(parsedError, I18n.t("admin_local.cashier_day_reprint_download_failed"));
        messageDialog(I18n.t("local.warning"), message);
    };

    const match = /^\s*(\d+)\/(\d+)\s*$/.exec(String(receiptNo || ""));
    if (!match) {
        showFailure(null);
        return;
    }

    const request = buildReprint_montax("receipt", cashRegister, {
        systemId: getMontaxSystemIdForSearch(cashRegister, null),
        fiscalDayNo: match[1],
        documentType,
        documentId: match[2],
        formatType: "FORMATTED"
    });
    if (!String(request?.requestOptions?.httpPath || "").trim()) {
        showFailure(null);
        return;
    }

    sendToCashier(cashRegister, request.text || "", request.pattern, false, (response) => {
        const parsed = parseMontaxResponse(response);
        const resultCode = parsed ? getMontaxResultCode(parsed) : "";
        if (!parsed || (resultCode && resultCode !== "SUCCESS") || !hasMontaxPrintableData(parsed)) {
            showFailure(parsed);
            return;
        }
        if (!printMontaxDocument(parsed)) {
            showFailure(null);
        }
    }, (response) => {
        showFailure(parseMontaxResponse(response));
    }, request.requestOptions);
};

// Storno request for the server (adminService/{restaurant}/storno). The server downloads the original receipt from the
// device and creates and closes the void receipt, so only the reason and the receipt texts are sent.
export const storno_montax = (data, def, cashRegister) => {
    const settings = parseCashRegisterSettings(resolveMontaxCashRegister(cashRegister) || cashRegister);
    const montaxStorno = {
        reason: /^[0-8]$/.test(String(data?.reason)) ? String(data.reason) : "0",
        thankYouText: resolveSetting(settings, ["montaxReceiptCustomText", "receiptCustomText"], "Köszönjük a vásárlást!")
    };
    const submitKeyRequest = buildSubmitKeyRequest(settings);
    if (submitKeyRequest) {
        montaxStorno.submitKeyRequest = submitKeyRequest;
    }
    const customer = {
        taxNumber: String(data?.customerTaxNum || "").trim(),
        name: String(data?.customerName || "").trim(),
        postCode: String(data?.customerPostCode || "").trim(),
        city: String(data?.customerTown || "").trim(),
        address: String(data?.customerAddress || "").trim()
    };
    if (customer.name) {
        montaxStorno.customer = customer;
    }
    def.resolve(JSON.stringify({ montaxStorno }));
};

export const isMontaxOpenDocumentConflict = (parsed) => {
    const resultCode = getMontaxResultCode(parsed);
    if (resultCode.includes("HAS_OPENED_DOCUMENT"))
        return true;
    return /already\s+opened\s+document/i.test(getMontaxResultDescription(parsed));
};

const getMontaxOpenDocuments = (parsed) => {
    const candidates = [parsed, parsed?.responses?.[0], parsed?.status, parsed?.responses?.[0]?.status];
    for (let i = 0; i < candidates.length; i++) {
        if (Array.isArray(candidates[i]?.openDocuments)) {
            return candidates[i].openDocuments
                .map(doc => ({
                    docId: doc?.docId ?? doc?.documentId,
                    fiscalDayNo: doc?.fiscalDayNo ?? doc?.fiscalDay,
                    type: doc?.type || doc?.documentType || "RECEIPT"
                }))
                .filter(doc => typeof doc.docId !== "undefined" && doc.docId !== null && String(doc.docId).trim() !== "");
        }
    }
    return [];
};

const buildMontaxCloseInterruptionRequest = (cashRegister, openDocument) => {
    const settings = parseCashRegisterSettings(cashRegister);
    const systemId = resolveSetting(settings, ["montaxSystemId", "systemId", "ap"], "");
    const documentDescriptor = {
        type: openDocument.type,
        docId: openDocument.docId,
        interrupted: true
    };
    if (typeof openDocument.fiscalDayNo !== "undefined" && openDocument.fiscalDayNo !== null && String(openDocument.fiscalDayNo) !== "")
        documentDescriptor.fiscalDayNo = openDocument.fiscalDayNo;
    const body = {
        "@type": "closeInterruption",
        documentDescriptor
    };
    if (systemId)
        body.systemId = systemId;
    return {
        text: JSON.stringify(body),
        requestOptions: {
            httpPath: "/doc/doc-close",
            httpMethod: "POST",
            appendNewLine: false,
            injectSystemIdFromDevice: true,
            headers: {
                "Content-Type": "application/json"
            }
        }
    };
};

// Reads the open documents from the device status and closes each of them with closeInterruption.
const closeMontaxOpenDocuments = (cashRegister, sendToCashier) => {
    const def = $.Deferred();
    const systemId = getMontaxSystemIdForSearch(cashRegister, null);
    const statusPath = `/system/status/${systemId === "{systemId}" ? systemId : encodeURIComponent(systemId)}`;

    const closeNext = (openDocuments, index) => {
        if (index >= openDocuments.length) {
            def.resolve();
            return;
        }
        const request = buildMontaxCloseInterruptionRequest(cashRegister, openDocuments[index]);
        sendToCashier(cashRegister, request.text, buildPattern(cashRegister), false, (response) => {
            const parsed = parseMontaxResponse(response);
            if (parsed && (getMontaxResultCode(parsed) === "SUCCESS" || parsed.success === true)) {
                closeNext(openDocuments, index + 1);
            } else {
                def.reject(parsed);
            }
        }, (response) => {
            def.reject(parseMontaxResponse(response));
        }, request.requestOptions);
    };

    sendToCashier(cashRegister, "", ".*", false, (response) => {
        const openDocuments = getMontaxOpenDocuments(parseMontaxResponse(response));
        if (!openDocuments.length) {
            def.reject(null);
            return;
        }
        closeNext(openDocuments, 0);
    }, (response) => {
        def.reject(parseMontaxResponse(response));
    }, buildMontaxListRequestOptions(statusPath));

    return def.promise();
};

// Sends a Montax message. If the fiscal day has an open document, that document is closed first and the message is retried once.
export const sendMontaxWithOpenDocumentRecovery = (cashRegister, text, pattern, requestOptions, sendToCashier, onSuccess, onError) => {
    const send = (retried) => {
        const handle = (response, isError) => {
            if (!retried && isMontaxOpenDocumentConflict(parseMontaxResponse(response))) {
                closeMontaxOpenDocuments(cashRegister, sendToCashier).done(() => {
                    send(true);
                }).fail((closeError) => {
                    messageDialog(I18n.t("local.warning"), getMontaxMessage(closeError, I18n.t("admin_local.cashier_day_close_failed")));
                });
                return;
            }
            if (isError)
                onError(response);
            else
                onSuccess(response);
        };
        sendToCashier(cashRegister, text, pattern, false, (response) => handle(response, false), (response) => handle(response, true), requestOptions);
    };
    send(false);
};

export const handleMontaxDayOpenResponse = (cashRegister, response, sendToCashier, isError = false) => {
    const parsed = parseMontaxResponse(response);
    const resultCode = getMontaxResultCode(parsed);

    if (isMontaxAlreadyOpen(parsed)) {
        confirmMontaxDownloadAndPrint(cashRegister, parsed, "dayOpen", sendToCashier);
        return true;
    }

    if (isError || !parsed || resultCode !== "SUCCESS") {
        messageDialog(I18n.t("local.warning"), getMontaxMessage(parsed, I18n.t("admin_local.cashier_day_already_opened")));
        return true;
    }

    saveMontaxReprintContext(cashRegister, "dayOpen", getMontaxReprintContext(parsed));
    printMontaxDocument(parsed);
    return true;
};

export const handleMontaxDayCloseReportResponse = (cashRegister, response, sendToCashier, isError = false) => {
    const parsed = parseMontaxResponse(response);

    if (isMontaxAlreadyClosed(parsed)) {
        confirmMontaxDownloadAndPrint(cashRegister, parsed, "dayClose", sendToCashier);
        return false;
    }

    if (isError || getMontaxResultCode(parsed) !== "SUCCESS") {
        messageDialog(I18n.t("local.warning"), getMontaxMessage(parsed, I18n.t("admin_local.cashier_day_close_requires_cash_register_report")));
        return false;
    }

    saveMontaxReprintContext(cashRegister, "dayClose", getMontaxReprintContext(parsed));
    printMontaxDocument(parsed);
    return true;
};

export const handleMontaxDayCloseResponse = (cashRegister, response, sendToCashier, isError = false) => {
    const parsed = parseMontaxResponse(response);

    if (!isError && getMontaxResultCode(parsed) === "SUCCESS") {
        saveMontaxReprintContext(cashRegister, "dayClose", getMontaxReprintContext(parsed));
        printMontaxDocument(parsed);
        messageDialog(I18n.t("local.warning"), I18n.t("admin_local.cashier_day_closed"));
        return true;
    }

    if (isMontaxAlreadyClosed(parsed)) {
        confirmMontaxDownloadAndPrint(cashRegister, parsed, "dayClose", sendToCashier);
        return true;
    }

    messageDialog(I18n.t("local.warning"), getMontaxMessage(parsed, I18n.t("admin_local.cashier_day_close_failed")));
    return true;
};

export const handleMontaxDayCloseWithCashRegisterReport = (
    cashRegister,
    cashRegisterReport,
    dayCloseText,
    pattern,
    requestOptions,
    sendToCashier
) => {
    if (!cashRegisterReport?.text)
        return false;

    sendMontaxWithOpenDocumentRecovery(cashRegister, cashRegisterReport.text, pattern, cashRegisterReport.requestOptions || requestOptions, sendToCashier, (reportResponse) => {
        if (!handleMontaxDayCloseReportResponse(cashRegister, reportResponse, sendToCashier, false)) {
            return;
        }

        sendMontaxWithOpenDocumentRecovery(cashRegister, dayCloseText, pattern, requestOptions, sendToCashier, (response) => {
            handleMontaxDayCloseResponse(cashRegister, response, sendToCashier, false);
        }, (response) => {
            handleMontaxDayCloseResponse(cashRegister, response, sendToCashier, true);
        });
    }, (reportResponse) => {
        handleMontaxDayCloseReportResponse(cashRegister, reportResponse, sendToCashier, true);
    });

    return true;
};

// Cash flow documents (cash in / cash out), pmtReason values are the keys of admin_local.cashin.montax / admin_local.cashout.montax.
const MONTAX_CASH_IN_REASONS = ["CHANGE", "CASHIER_DEPOSIT", "COLLECTION", "LOTTERY_TICKET_SALE", "DEPOSIT", "DEFICIT", "TIP", "OTHER_CASH_IN"];
const MONTAX_CASH_OUT_REASONS = ["SKIMMING", "CASHIER_WITHDRAWAL", "VOUCHER_OUT", "GIFT_CARD_OUT", "SALARY_PAYMENT", "ADVANCE_PAYMENT", "POSTAL_FEE", "OTHER_FEES", "PROCUREMENT", "TURNOVER_WITHDRAWAL", "OTHER_CASH_OUT"];

const buildMontaxCashFlowRequest = (direction, reason, amount, cashierType) => {
    const reasons = direction === "out" ? MONTAX_CASH_OUT_REASONS : MONTAX_CASH_IN_REASONS;
    const value = Math.abs(Number(String(amount ?? "").replace(",", ".")));
    if (!reasons.includes(reason) || !Number.isFinite(value) || value <= 0)
        return {};

    const settings = parseCashRegisterSettings(cashierType);
    const body = {
        "@type": "createCashFlowReport",
        systemId: resolveSetting(settings, ["montaxSystemId", "systemId", "ap"], ""),
        pmtReason: reason,
        otherTitle: reason.startsWith("OTHER_") ? I18n.t(`admin_local.${direction === "out" ? "cashout" : "cashin"}.montax.${reason}`) : null,
        cashDrawer: [{
            moneyCat: resolveSetting(settings, ["montaxMoneyCat", "moneyCat"], "CASH"),
            name: resolveSetting(settings, ["montaxDrawerName", "drawerName"], "Készpénz"),
            moneySubCat: null,
            // Payouts are sent with a negative amount.
            moneyAmount: formatMoney(direction === "out" ? -value : value),
            currency: resolveSetting(settings, ["montaxCurrency", "currency"], "HUF")
        }],
        createDownloadInfo: true
    };
    const submitKeyRequest = buildSubmitKeyRequest(settings);
    if (submitKeyRequest)
        body.submitKeyRequest = submitKeyRequest;

    return {
        text: JSON.stringify(body),
        pattern: buildPattern(cashierType),
        requestOptions: buildRequestOptions(cashierType, ["montaxCashFlowHttpPath", "cashFlowHttpPath"], ["montaxCashFlowHttpMethod", "cashFlowHttpMethod"])
    };
};

export const cashin_montax = (reason, amount, cashierType) => buildMontaxCashFlowRequest("in", reason, amount, cashierType);

export const cashout_montax = (reason, amount, cashierType) => buildMontaxCashFlowRequest("out", reason, amount, cashierType);

// Sends the cash flow document (closing an open document first if needed) and prints the returned slip.
export const sendMontaxCashFlow = (cashRegister, request, sendToCashier) => {
    const showFailure = (parsed) => {
        // A cash flow document can only be created on an open fiscal day.
        const message = getMontaxResultCode(parsed) === "FISCAL_DAY_CLOSED"
            ? I18n.t("admin_local.cashier_day_not_open")
            : getMontaxMessage(parsed, I18n.t("admin_local.kiosk_cash_register_communication_unsuccessful"));
        messageDialog(I18n.t("local.warning"), message);
    };
    const handle = (response, isError) => {
        const parsed = parseMontaxResponse(response);
        const resultCode = getMontaxResultCode(parsed);
        // SUCCESS_WITH_NO_QR / SUCCESS_WITH_NO_UPLOAD: the document is recorded, only the QR or the upload is missing.
        const recorded = resultCode === "SUCCESS" || resultCode.startsWith("SUCCESS_WITH_");
        if (!parsed || !recorded || (isError && !resultCode.startsWith("SUCCESS"))) {
            showFailure(parsed);
            return;
        }
        if (!hasMontaxPrintableData(parsed) || !printMontaxDocument(parsed))
            messageDialog(I18n.t("local.warning"), I18n.t("admin_local.cashier_cash_flow_print_failed"));
    };
    sendMontaxWithOpenDocumentRecovery(cashRegister, request.text, request.pattern, request.requestOptions, sendToCashier, (response) => handle(response, false), (response) => handle(response, true));
};
